import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PublicClient, PublicComponents } from '../openapi/openapi';

@Injectable({
  providedIn: 'root',
})
export class RemoteConfigService {
  private readonly client = inject(PublicClient);

  private readonly data = signal<PublicComponents.AlixResponse>({ version: '' });

  async load() {
    this.data.set(await firstValueFrom(this.client.alixRead()));
  }

  get isLoaded() {
    return !!this.data().version;
  }

  version() {
    return this.data().version;
  }

  get(key: string) {
    return this.data()[key] as PublicComponents.AlixResponse[string] | undefined;
  }

  getOrDefault<T>(key: string, defaultValue: T) {
    return this.get(key) ?? defaultValue;
  }

  getAs<T>(key: string, parser: (value: string | number | boolean) => T) {
    const value = this.get(key);

    return value === null || value === undefined ? undefined : parser(value);
  }

  getAsString(key: string) {
    return this.getAs(key, String);
  }

  getAsNumber(key: string) {
    return this.getAs(key, Number);
  }

  getAsArray(key: string, separator = ',') {
    const value = this.get(key);
    if (typeof value !== "string" || !value.trim()) {
      return undefined;
    }

    return ((result) => result?.length ? result : undefined)(
      value?.split(separator).map(s => s.trim()).filter(Boolean) ?? []
    );
  }

  isTrue(key: string) {
    return this.get(key) === 'true';
  }
}
